import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { BarChart3, TrendingUp, Eye, DollarSign, Gavel, Users, Star, Loader2, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { api } from "@/api/client";
import { logger } from "@/utils/logger";

const BreederInsights = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30d");

  useEffect(() => {
    fetchInsights();
  }, [period]);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/breeder/insights?period=${period}`);
      setData(res.data);
    } catch (err) {
      logger.error("Failed to fetch insights:", err);
    } finally {
      setLoading(false);
    }
  };

  const sortedSpecies = useMemo(() => {
    if (!data) return [];
    const sb = data.species_breakdown || {};
    const entries = Object.entries(sb);
    if (entries.length === 0) return [];
    const total = Object.values(sb).reduce((a, b) => a + b, 0);
    return entries
      .sort(([, a], [, b]) => b - a)
      .map(([species, count]) => ({ species, count, pct: Math.round((count / total) * 100) }));
  }, [data]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-[#71717A] text-center py-8">Unable to load insights.</p>;
  }

  const { overview, auctions, engagement, top_listings, species_breakdown, recent_bids, marketplace_comparison } = data;

  const priceComparison = (marketplace_comparison?.platform_avg_sale_price || 0) > 0
    ? (((marketplace_comparison?.your_avg_sale_price || 0) - marketplace_comparison.platform_avg_sale_price) / marketplace_comparison.platform_avg_sale_price * 100).toFixed(0)
    : 0;

  return (
    <div className="space-y-6" data-testid="breeder-insights">
      {/* Period Toggle */}
      <div className="flex items-center justify-between">
        <h3 className="text-xl text-[#EDEDED] flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-[#D4AF37]" />
          Seller Insights
        </h3>
        <div className="flex gap-1 bg-[#18181B] rounded-lg p-1" data-testid="period-toggle">
          {[
            { value: "7d", label: "7D" },
            { value: "30d", label: "30D" },
            { value: "90d", label: "90D" },
            { value: "all", label: "All" },
          ].map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                period === p.value
                  ? "bg-[#D4AF37] text-black font-medium"
                  : "text-[#A1A1AA] hover:text-[#EDEDED]"
              }`}
              data-testid={`period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="key-metrics">
        <MetricCard
          icon={<Eye className="w-4 h-4" />}
          label="Total Views"
          value={overview.total_views.toLocaleString()}
          color="text-blue-400"
        />
        <MetricCard
          icon={<DollarSign className="w-4 h-4" />}
          label="Revenue"
          value={`$${overview.total_revenue.toLocaleString()}`}
          color="text-[#22C55E]"
        />
        <MetricCard
          icon={<Gavel className="w-4 h-4" />}
          label="Bids Received"
          value={auctions.total_bids_received.toLocaleString()}
          color="text-[#D4AF37]"
        />
        <MetricCard
          icon={<Users className="w-4 h-4" />}
          label="Inquiries"
          value={engagement.inquiries.toLocaleString()}
          color="text-purple-400"
        />
      </div>

      {/* Listings & Auction Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Listings Overview */}
        <div className="glass-card p-5">
          <h4 className="text-sm text-[#71717A] uppercase tracking-wider mb-4">Listings</h4>
          <div className="space-y-3">
            <StatRow label="Active Listings" value={overview.active_listings} total={overview.total_listings} />
            <StatRow label="Total Sales" value={overview.total_sales} />
            <StatRow label="Avg Sale Price" value={`$${overview.avg_sale_price.toLocaleString()}`} />
          </div>
        </div>

        {/* Auction Performance */}
        <div className="glass-card p-5">
          <h4 className="text-sm text-[#71717A] uppercase tracking-wider mb-4">Auctions</h4>
          <div className="space-y-3">
            <StatRow label="Active Auctions" value={auctions.active} total={auctions.total} />
            <StatRow label="Auction Revenue" value={`$${auctions.total_revenue.toLocaleString()}`} />
            <StatRow label="Avg Bids/Auction" value={auctions.avg_bids_per_auction} />
            <StatRow label="Conversion Rate" value={`${auctions.conversion_rate}%`} highlight={auctions.conversion_rate > 50} />
          </div>
        </div>
      </div>

      {/* Marketplace Comparison */}
      <div className="glass-card p-5" data-testid="marketplace-comparison">
        <h4 className="text-sm text-[#71717A] uppercase tracking-wider mb-4">Marketplace Comparison</h4>
        <div className="flex items-center gap-6">
          <div className="flex-1">
            <p className="text-xs text-[#71717A]">Your Avg Sale Price</p>
            <p className="text-2xl text-[#EDEDED] font-semibold">${marketplace_comparison.your_avg_sale_price.toLocaleString()}</p>
          </div>
          <div className="flex-1">
            <p className="text-xs text-[#71717A]">Platform Average</p>
            <p className="text-2xl text-[#A1A1AA]">${marketplace_comparison.platform_avg_sale_price.toLocaleString()}</p>
          </div>
          <div className="flex-1">
            {Number(priceComparison) !== 0 && (
              <div className={`flex items-center gap-1 ${Number(priceComparison) > 0 ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
                {Number(priceComparison) > 0 ? <ArrowUpRight className="w-5 h-5" /> : <ArrowDownRight className="w-5 h-5" />}
                <span className="text-xl font-bold">{Math.abs(priceComparison)}%</span>
              </div>
            )}
            <p className="text-xs text-[#71717A]">{Number(priceComparison) >= 0 ? "Above" : "Below"} average</p>
          </div>
        </div>
      </div>

      {/* Top Listings & Species */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Top Performing */}
        <div className="glass-card p-5">
          <h4 className="text-sm text-[#71717A] uppercase tracking-wider mb-4 flex items-center gap-2">
            <Star className="w-3.5 h-3.5 text-[#D4AF37]" /> Top Performing
          </h4>
          {top_listings.length === 0 ? (
            <p className="text-sm text-[#52525B]">No listings yet</p>
          ) : (
            <div className="space-y-2">
              {top_listings.map((listing, idx) => (
                <Link
                  key={listing.listing_id}
                  to={`/listing/${listing.listing_id}`}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#18181B] transition-colors"
                >
                  <span className="w-5 h-5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] text-xs flex items-center justify-center font-bold">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#EDEDED] truncate">{listing.title}</p>
                    <p className="text-xs text-[#71717A] capitalize">{listing.species}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[#A1A1AA]">{listing.views} views</p>
                    <p className="text-xs text-[#D4AF37]">${listing.price?.toLocaleString()}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Species Breakdown */}
        <div className="glass-card p-5">
          <h4 className="text-sm text-[#71717A] uppercase tracking-wider mb-4">Species Breakdown</h4>
          {Object.keys(species_breakdown).length === 0 ? (
            <p className="text-sm text-[#52525B]">No data</p>
          ) : (
            <div className="space-y-3">
              {sortedSpecies.map(({ species, count, pct }) => (
                    <div key={species}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-[#EDEDED] capitalize">{species}</span>
                        <span className="text-[#71717A]">{count} ({pct}%)</span>
                      </div>
                      <div className="h-1.5 bg-[#27272A] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#D4AF37] rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Bid Activity */}
      {recent_bids.length > 0 && (
        <div className="glass-card p-5" data-testid="recent-bids">
          <h4 className="text-sm text-[#71717A] uppercase tracking-wider mb-4 flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5 text-[#22C55E]" /> Recent Bid Activity (7 days)
          </h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {recent_bids.map((bid) => (
              <div key={bid.bid_id} className="flex items-center justify-between p-2 bg-[#0A0A0A] rounded-lg">
                <div>
                  <p className="text-sm text-[#D4AF37] font-semibold">${bid.amount.toLocaleString()}</p>
                  <p className="text-xs text-[#71717A]">{new Date(bid.created_at).toLocaleDateString()}</p>
                </div>
                <Link
                  to={`/auctions/${bid.auction_id}`}
                  className="text-xs text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                >
                  View Auction →
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// Sub-components
const MetricCard = ({ icon, label, value, color }) => (
  <div className="glass-card p-4 text-center">
    <div className={`${color} mb-2 flex justify-center`}>{icon}</div>
    <p className="text-2xl text-[#EDEDED] font-bold">{value}</p>
    <p className="text-xs text-[#71717A] mt-1">{label}</p>
  </div>
);

const StatRow = ({ label, value, total, highlight }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-[#A1A1AA]">{label}</span>
    <span className={`text-sm font-medium ${highlight ? "text-[#22C55E]" : "text-[#EDEDED]"}`}>
      {value}{total !== undefined && <span className="text-[#71717A]">/{total}</span>}
    </span>
  </div>
);

export default BreederInsights;
